import type { ImageBlockModel } from '@blocksuite/hive-model';
import { humanFileSize } from '@blocksuite/hive-shared/utils';
import { WithDisposable } from '@blocksuite/global/lit';
import { modelContext, ShadowlessElement } from '@blocksuite/std';
import { consume } from '@lit/context';
import { css, html } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { FailedImageIcon, LoadedImageIcon, LoadingIcon } from '../styles.js';

export const SURFACE_IMAGE_CARD_WIDTH = 220;
export const SURFACE_IMAGE_CARD_HEIGHT = 122;
export const NOTE_IMAGE_CARD_WIDTH = 752;
export const NOTE_IMAGE_CARD_HEIGHT = 78;

export class ImageBlockFallbackCard extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    .hive-image-fallback-card-container {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .hive-image-fallback-card {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      background-color: var(--hive-background-secondary-color, #f4f4f5);
      border-radius: 8px;
      border: 1px solid var(--hive-background-tertiary-color, #eee);
      padding: 12px;
    }

    .hive-image-fallback-card-content {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--hive-placeholder-color);
      text-align: justify;
      font-family: var(--hive-font-family);
      font-size: var(--hive-font-sm);
      font-style: normal;
      font-weight: 600;
      line-height: var(--hive-line-height);
      user-select: none;
    }

    .hive-image-card-size {
      overflow: hidden;
      padding-top: 12px;
      color: var(--hive-text-secondary-color);
      text-overflow: ellipsis;
      font-size: 10px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      user-select: none;
    }
  `;

  override render() {
    const { mode, loading, error, model } = this;

    const isEdgeless = mode === 'edgeless';
    const width = isEdgeless
      ? `${SURFACE_IMAGE_CARD_WIDTH}px`
      : `${NOTE_IMAGE_CARD_WIDTH}px`;
    const height = isEdgeless
      ? `${SURFACE_IMAGE_CARD_HEIGHT}px`
      : `${NOTE_IMAGE_CARD_HEIGHT}px`;

    const rotate = isEdgeless ? model.rotate : 0;

    const cardStyleMap = styleMap({
      transform: `rotate(${rotate}deg)`,
      transformOrigin: 'center',
      width,
      height,
    });

    const titleIcon = loading
      ? LoadingIcon
      : error
        ? FailedImageIcon
        : LoadedImageIcon;

    const titleText = loading
      ? 'Loading image...'
      : error
        ? 'Image loading failed.'
        : 'Image';

    const size =
      !!model.props.size && model.props.size > 0
        ? humanFileSize(model.props.size, true, 0)
        : null;

    return html`
      <div class="hive-image-fallback-card-container">
        <div
          class="hive-image-fallback-card drag-target"
          style=${cardStyleMap}
        >
          <div class="hive-image-fallback-card-content">
            ${titleIcon}
            <span class="hive-image-fallback-card-title-text"
              >${titleText}</span
            >
          </div>
          <div class="hive-image-card-size">${size}</div>
        </div>
      </div>
    `;
  }

  @property({ attribute: false })
  accessor error!: boolean;

  @property({ attribute: false })
  accessor loading!: boolean;

  @property({ attribute: false })
  accessor mode!: 'page' | 'edgeless';

  @consume({ context: modelContext })
  accessor model!: ImageBlockModel;
}

declare global {
  interface HTMLElementTagNameMap {
    'hive-image-fallback-card': ImageBlockFallbackCard;
  }
}
